import { useEffect, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../api/client.js';
import { useRegistrationMode } from '../api/queries.js';
import LandingLogo from '../components/landing/LandingLogo.js';
import type { UserDto } from '@b4b/shared';

type Mode = 'login' | 'register';

export default function Login() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  // signed-out deep link (/c/:id, /settings…) bounced here by App → go back there after login
  const from = (location.state as { from?: string } | null)?.from;
  const registration = useRegistrationMode();
  const registrationMode = registration.data?.mode;
  const canRegister = registrationMode !== 'closed';

  // an invite link (/login?mode=register&invite=B4B-XXXXXX) opens the register form with the code filled in
  const inviteFromLink = searchParams.get('invite') ?? '';
  const [mode, setMode] = useState<Mode>(
    searchParams.get('mode') === 'register' || inviteFromLink ? 'register' : 'login',
  );
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [inviteCode, setInviteCode] = useState(inviteFromLink);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // registration turned out to be closed → fall back to login
  useEffect(() => {
    if (!canRegister && mode === 'register') setMode('login');
  }, [canRegister, mode]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const url = mode === 'login' ? '/api/auth/login' : '/api/auth/register';
      const body =
        mode === 'login'
          ? { email, password }
          : { email, password, inviteCode: inviteCode || undefined };
      const res = await api.post<{ user: UserDto }>(url, body);
      qc.setQueryData(['me'], res);
      navigate(from && from.startsWith('/') && from !== '/login' ? from : '/', { replace: true });
    } catch (err) {
      const code = err instanceof ApiError ? err.code : 'generic';
      setError(code);
    } finally {
      setBusy(false);
    }
  };

  const input =
    'w-full rounded-xl border border-neutral-300 bg-white px-4 py-3 dark:border-white/10 dark:bg-slate-800';
  const secondary = 'text-neutral-500 dark:text-slate-400';

  return (
    <div className="flex min-h-screen flex-col bg-neutral-50 text-neutral-900 dark:bg-slate-900 dark:text-slate-100" data-page="login">
      <header className="mx-auto flex h-14 w-full max-w-[1120px] items-center justify-between px-5 sm:h-16 sm:px-12">
        <LandingLogo />
        <Link to="/" className={`flex min-h-[44px] items-center text-sm font-medium hover:text-accent ${secondary}`}>
          {t('auth.backToLanding')}
        </Link>
      </header>

      <div className="flex flex-1 items-center justify-center px-4 pb-16">
        <form onSubmit={submit} className="w-full max-w-sm space-y-4" data-mode={mode}>
          <div className="text-center">
            <h1 className="text-2xl font-bold">{t(`auth.title.${mode}`)}</h1>
            <p className={`mt-1 text-sm ${secondary}`}>{t('app.tagline')}</p>
          </div>

          <input
            type="email"
            required
            autoComplete="email"
            aria-label={t('auth.email')}
            placeholder={t('auth.email')}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={input}
          />
          <input
            type="password"
            required
            autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
            aria-label={t('auth.password')}
            placeholder={t('auth.password')}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className={input}
          />
          {mode === 'register' && registrationMode !== undefined && registrationMode !== 'open' && (
            <input
              type="text"
              required={registrationMode === 'invite_code'}
              autoComplete="off"
              aria-label={t('auth.inviteCode')}
              placeholder={t('auth.inviteCode')}
              value={inviteCode}
              onChange={(e) => setInviteCode(e.target.value)}
              className={input}
            />
          )}
          {mode === 'register' && registrationMode === 'invite_code' && (
            <p className={`text-sm ${secondary}`}>{t('auth.inviteNote')}</p>
          )}

          {error && (
            <p className="text-sm text-red-600 dark:text-red-400" role="alert">
              {t(`auth.errors.${error}`, t('auth.errors.generic'))}
            </p>
          )}

          <button
            type="submit"
            disabled={busy}
            className="w-full rounded-xl bg-accent px-4 py-3 font-semibold text-white hover:bg-accent-strong disabled:opacity-50"
          >
            {mode === 'login' ? t('auth.login') : t('auth.register')}
          </button>

          <div className={`space-y-1 text-center text-sm ${secondary}`}>
            {canRegister ? (
              <button
                type="button"
                className="min-h-[44px] underline"
                onClick={() => {
                  setMode(mode === 'login' ? 'register' : 'login');
                  setError(null);
                }}
              >
                {mode === 'login' ? t('auth.noAccount') : t('auth.haveAccount')}
              </button>
            ) : (
              <p>{t('auth.closedNote')}</p>
            )}
            {mode === 'login' && <p>{t('auth.forgotPassword')}</p>}
          </div>
        </form>
      </div>
    </div>
  );
}
